"use client";

import { svgImages } from "@/lib/svgImages";
import styles from "./staticVsDynamicV2.module.scss";
import { useEffect, useRef, useState } from "react";
import CustomSpinner from "../spinner/spinner";
import TitleWithDescriptionV2 from "../titleWithDescriptionV2/titleWithDescriptionV2";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/dist/ScrollTrigger";
import { getStaticVsDynamicData } from "@/lib/wordpressApi";

const StaticVsDynamicV2 = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);
  const staticAdvantagesRef = useRef<HTMLDivElement>(null);
  const dynamicAdvantagesRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const data = await getStaticVsDynamicData();
        setData(data);
      } catch (error) {
        console.error("Error fetching data:", error);
        // Handle error state if needed
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    gsap.registerPlugin(ScrollTrigger);
    if (!staticAdvantagesRef.current || !dynamicAdvantagesRef.current) return;

    // Animate main sections
    const staticVsDynamicContainer = document.querySelector(
      `.${styles.staticVsDynamicContainer}`
    );
    const staticIconTitle = document.querySelector(
      `.${styles.staticQr} .${styles.iconTitleContainer}`
    );
    const staticMainDesc = document.querySelector(
      `.${styles.staticQr} .${styles.description}`
    );
    const dynamicIconTitle = document.querySelector(
      `.${styles.dynamicQr} .${styles.iconTitleContainer}`
    );
    const dynamicMainDesc = document.querySelector(
      `.${styles.dynamicQr} .${styles.description}`
    );

    // Animate static and dynamic headers
    gsap
      .timeline({
        scrollTrigger: {
          trigger: staticVsDynamicContainer,
          start: "top 80%",
          toggleActions: "play none none none",
        },
      })
      .from([staticIconTitle, staticMainDesc], {
        opacity: 0,
        y: 30,
        duration: 0.5,
        stagger: 0.2,
        ease: "power2.out",
      })
      .from(
        [dynamicIconTitle, dynamicMainDesc],
        {
          opacity: 0,
          y: 20,
          duration: 0.4,
          stagger: 0.2,
          ease: "power2.out",
        },
        "-=0.2"
      );

    // Animate advantages
    const advantageContainers = document.querySelectorAll(
      `.${styles.advantageItemContainer}`
    );

    advantageContainers.forEach((container) => {
      const staticAdvantage = container.querySelector(
        `.${styles.staticQr} .${styles.advantageItem}`
      );
      const dynamicAdvantage = container.querySelector(
        `.${styles.dynamicQr} .${styles.advantageItem}`
      );

      if (staticAdvantage && dynamicAdvantage) {
        const staticTitle = staticAdvantage.querySelector(
          `.${styles.advantageTitle}`
        );
        const staticDesc = staticAdvantage.querySelector(
          `.${styles.advantageDesc}`
        );
        const dynamicTitle = dynamicAdvantage.querySelector(
          `.${styles.advantageTitle}`
        );
        const dynamicDesc = dynamicAdvantage.querySelector(
          `.${styles.advantageDesc}`
        );

        gsap
          .timeline({
            scrollTrigger: {
              trigger: container,
              start: "top 80%",
              toggleActions: "play none none none",
            },
          })
          .from([staticTitle, dynamicTitle], {
            opacity: 0,
            y: 30,
            duration: 0.5,
            stagger: 0.2,
            ease: "power2.out",
          })
          .from(
            [staticDesc, dynamicDesc],
            {
              opacity: 0,
              y: 20,
              duration: 0.4,
              stagger: 0.2,
              ease: "power2.out",
            },
            "-=0.2"
          );
      }
    });

    return () => {
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
    };
  }, [data]);

  return (
    <>
      {loading ? (
        <>
          <CustomSpinner />
        </>
      ) : (
        <>
          <TitleWithDescriptionV2
            title={`QR Codes <br />Static vs Dynamic`}
            description={`Dynamic QR codes are more flexible and can be updated to reflect changes in your business. Static QR codes, on the other hand, are fixed and cannot be changed.`}
          />
          <div className={`${styles["staticVsDynamic"]}`} ref={containerRef}>
            <div className={`${styles["staticVsDynamicContainer"]}`}>
              <div className={`${styles["staticQr"]}`}>
                <div className={`${styles["iconTitleContainer"]}`}>
                  <div
                    className={`${styles["icon"]}`}
                    dangerouslySetInnerHTML={{ __html: svgImages.staticFalse }}
                  />
                  <h3 className={`${styles["title"]} font-size-48`}>
                    {data?.acf.static_title}
                  </h3>
                </div>
                <div
                  className={`${styles["description"]} font-size-18 line-height-content`}
                >
                  {data?.acf.static_subtitle}
                </div>
              </div>
              <div className={`${styles["dynamicQr"]}`}>
                <div className={`${styles["iconTitleContainer"]}`}>
                  <div
                    className={`${styles["icon"]}`}
                    dangerouslySetInnerHTML={{ __html: svgImages.dynamicTrue }}
                  />
                  <h3 className={`${styles["title"]} font-size-48`}>
                    {data?.acf.dynamic_title}
                  </h3>
                </div>
                <div
                  className={`${styles["description"]} font-size-18 line-height-content`}
                >
                  {data?.acf.dynamic_subtitle}
                </div>
              </div>
            </div>
            <div className={`${styles["advantagesContainer"]}`}>
              {data?.acf.dynamic_advantages &&
                data?.acf.dynamic_advantages.map((item, index) => (
                  <div
                    className={`${styles["advantageItemContainer"]}`}
                    key={`advantage-${index}`}
                  >
                    <div className={`${styles["staticQr"]}`}>
                      <div
                        className={`${styles["advantages"]}`}
                        ref={staticAdvantagesRef}
                      >
                        <div className={`${styles["advantageItem"]}`}>
                          <div className={`${styles["text"]}`}>
                            <div
                              className={`${styles["advantageTitle"]} font-size-24`}
                            >
                              {
                                data?.acf.static_advantages[index]
                                  .advantage_title
                              }
                            </div>
                            <div
                              className={`${styles["advantageDesc"]} font-size-18 line-height-content`}
                            >
                              {
                                data?.acf.static_advantages[index]
                                  .advantage_desc
                              }
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className={`${styles["dynamicQr"]}`}>
                      <div
                        className={`${styles["advantages"]}`}
                        ref={dynamicAdvantagesRef}
                      >
                        <div className={`${styles["advantageItem"]}`}>
                          <div className={`${styles["text"]}`}>
                            <div
                              className={`${styles["advantageTitle"]} font-size-24`}
                            >
                              {item.advantage_title}
                            </div>
                            <div
                              className={`${styles["advantageDesc"]} font-size-18 line-height-content`}
                            >
                              {item.advantage_desc}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default StaticVsDynamicV2;
